import cn from 'clsx'
import debounce from 'lodash/debounce'
import isEqual from 'lodash/isEqual'

import React, {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from 'react'
import { useDispatch, useSelector } from 'hooks/use-redux'
import type { Playlist, PlaylistId, PlaylistTag } from 'types/playlist'
import type { State } from 'types/redux'
import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { updatePlaylistTags } from '@disco/state-management/actions/playlist-actions'
import { createPlaylistTag } from '@disco/state-management/actions/PlaylistTagActions'
import { COLOR_INDICATORS } from '@disco/constants/FeatureTypes'
import { useFeatureEnabled } from '@disco/state-management/selectors/business'
import { getPlaylist } from '@disco/state-management/selectors/playlists'
import { getPlaylistTags } from '@disco/state-management/selectors/tags'
import { COLORS } from '@disco/utils/color-indicators'
import ColorIndicatorsAlert from 'components/common/color-indicators-alert'
import { Box } from 'components/ds'
import Button from 'components/ui/button'
import DiscoModal from 'components/ui/disco-modal'
import Heading from 'components/ui/heading'
import TagsInput from 'components/ui/tags-input'
import TextOverflow from 'components/ui/text-overflow'
import useEventListener from 'hooks/use-event-listener'
import styles from './index.module.css'

const getNodeHeight = (node: HTMLDivElement | null | undefined): number => {
  if (node) {
    const { height } = node.getBoundingClientRect()
    return height
  }

  return 0
}

const MENU_ITEM_HEIGHT = 32
type PlaylistTagId = PlaylistTag['id']
type PlaylistTagName = PlaylistTag['name']
type Props = {
  playlistId: PlaylistId
  onClose: () => void
}

/**
 * PlaylistTagsModal component for managing playlist tags
 *
 * A modal interface for adding and removing tags from playlists. Features include
 * tag search, creation of new tags, color indicators (if enabled), and a sticky
 * header that adapts to content height. Tracks changes and enables saving when
 * modifications are made.
 *
 * @param props - Component props
 * @param props.playlistId - ID of the playlist to manage tags for
 * @param props.onClose - Callback function called when modal is closed
 * @returns JSX element containing the modal interface for tag management
 */
const PlaylistTagsModal = (props: Props): React.ReactNode => {
  const dispatch = useDispatch()
  // selector result asserted non-null to preserve prior behavior (was typed Playlist)
  const playlist = useSelector((state: State) =>
    getPlaylist(state, {
      id: props.playlistId,
    }),
  )!
  const playlistTags: PlaylistTag[] = useSelector(getPlaylistTags)
  const colorIndicatorsEnabled = useFeatureEnabled(COLOR_INDICATORS)
  const [selectedTags, setSelectedTags] = useState<PlaylistTagName[]>([])
  useEffect(() => {
    setSelectedTags(playlist.tags ?? [])
  }, [playlist.tags])
  const [query, setQuery] = useState<string>('')

  const updateSearchQuery = (value: string) => {
    setQuery(value)
  }

  const containerRef = useRef(null)
  const headerRef = useRef(null)
  const [isHeaderSticky, setIsHeaderSticky] = useState<boolean>(true)
  const handleResize = useCallback(() => {
    if (containerRef.current && headerRef.current) {
      const containerHeight = getNodeHeight(containerRef.current)
      const headerHeight = getNodeHeight(headerRef.current)

      if (containerHeight - headerHeight > MENU_ITEM_HEIGHT * 5) {
        setIsHeaderSticky(true)
      } else {
        setIsHeaderSticky(false)
      }
    }
  }, [containerRef, headerRef])
  useEventListener('resize', debounce(handleResize, 300))
  useLayoutEffect(handleResize, [selectedTags])

  const selected = (tag: PlaylistTag): boolean =>
    selectedTags.includes(tag.name)

  const unselected = (tag: PlaylistTag): boolean => !selected(tag)

  const tagExists = (name: string): boolean =>
    playlistTags.some(
      (tag: PlaylistTag) =>
        tag.name.toLowerCase() === name.toLowerCase().trim(),
    )

  const searchByName = (tag: PlaylistTag) =>
    !query || tag.name.toLowerCase().includes(query.toLowerCase())

  const filteredTags: PlaylistTag[] = playlistTags
    .filter(unselected)
    .filter(searchByName)

  const mapPlaylistTag = (name: PlaylistTagName) => {
    const tag = playlistTags.find(tag => tag.name === name)
    return {
      color: colorIndicatorsEnabled ? tag?.color : null,
      name: name,
      value: name,
    }
  }

  const [saving, setSaving] = useState<boolean>(false)

  const createTag = async () => {
    setSaving(true)
    const name = query.trim()

    try {
      await dispatch(createPlaylistTag(name))
      setSelectedTags([...selectedTags, name])
      updateSearchQuery('')
    } catch (error) {}

    setSaving(false)
  }

  const handleTagClick = (name: PlaylistTagName) => () => {
    setSelectedTags([...selectedTags, name])
    updateSearchQuery('')
  }

  const handleSubmit = () => {
    dispatch(updatePlaylistTags(playlist, [...selectedTags]))
    dispatch(sendMixpanelEvent('Add playlist tag in explorer'))
    props.onClose()
  }

  return (
    <DiscoModal onRequestClose={props.onClose}>
      <div className="modal-width--400">
        <div className={styles.playlistTagsModal}>
          <div className={styles.playlistTagsModalScroll} ref={containerRef}>
            <div className={styles.playlistTagsModalScrollContent}>
              <div
                className={cn(styles.playlistTagsModalHeader, {
                  [styles.playlistTagsModalHeaderSticky]: isHeaderSticky,
                })}
                ref={headerRef}
              >
                <Box mb="4">
                  <Heading size={600}>Tag playlist</Heading>
                  <ColorIndicatorsAlert />
                </Box>
                <TagsInput
                  autoFocus
                  value={query}
                  tags={selectedTags.map(mapPlaylistTag)}
                  onChange={setSelectedTags}
                  onInputChange={updateSearchQuery}
                />
              </div>
              <div className={styles.playlistTagsModalMenu}>
                {filteredTags.map((tag: PlaylistTag, index: number) => (
                  <div
                    key={index}
                    className={styles.playlistTagsModalItem}
                    onClick={handleTagClick(tag.name)}
                  >
                    {colorIndicatorsEnabled && tag.color && (
                      <div
                        className={styles.playlistTagsModalItemColor}
                        style={{
                          background: COLORS[tag.color],
                        }}
                      />
                    )}
                    <TextOverflow content={tag.name} />
                  </div>
                ))}
                {filteredTags.length === 0 && query && !tagExists(query) && (
                  <div
                    className={cn(styles.playlistTagsModalItem, {
                      [styles.playlistTagsModalItemDisabled]: saving,
                    })}
                    onClick={saving ? undefined : createTag}
                  >
                    <TextOverflow
                      content={`${query} (${
                        saving ? 'creating...' : 'create new tag'
                      })`}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
          <div className={styles.playlistTagsModalFooter}>
            <div>
              <Button
                appearance="inline-link"
                caption="Manage tags"
                link="/settings/playlist-tags"
              />
            </div>
            <div>
              <Button
                appearance="primary"
                caption="Save"
                disabled={isEqual(playlist.tags, selectedTags)}
                onClick={handleSubmit}
              />
            </div>
          </div>
        </div>
      </div>
    </DiscoModal>
  )
}

export default PlaylistTagsModal
